/**
 * Tipos y normalizacion del contrato publico de payment-api que consume el widget.
 *
 * Fuente de verdad: `contracts/openapi/payment-api.v1.yaml` (schemas `PaymentIntent`,
 * `CheckoutConfig` y `Problem`) y los DTO reales de payment-api. El widget habla
 * snake_case con la API y no reinventa nombres de campo.
 *
 * El cuerpo que devuelve la API es *plano* (`gateway_id`, `redirect_url` y
 * `payment_methods` viven en niveles distintos a los que necesita la UI del widget),
 * por eso `normalizePaymentIntent()` proyecta la respuesta a la forma interna que
 * comparten la carcasa y las estrategias de pasarela. Toda la validacion de tipos
 * ocurre aqui: mas adentro nadie vuelve a mirar `unknown`.
 *
 * PCI DSS SAQ A: ninguna de estas estructuras transporta datos de tarjeta. Lo unico
 * que la API expone del medio de pago son metadatos no sensibles (marca, ultimos
 * cuatro digitos, expiracion) que ya vienen despersonalizados desde la pasarela.
 */
import { sanitizeLogoUrl, sanitizeTenantTheme } from '../theme/applyTenantTheme';

/**
 * Estados de `payment_intents.status`.
 *
 * <p><strong>Esta lista tiene que estar COMPLETA, y no es una cuestion de estilo.</strong>
 * `asIntentStatus` devuelve `null` ante un estado que no reconoce, y con el falla el parseo del
 * intent ENTERO: el comprador no ve la pantalla que le corresponde sino un error de carga. O sea
 * que un estado nuevo en el backend, sin tocar nada aqui, convierte una pantalla en una caida.
 *
 * <p>Asi entro `REVERSED`: el commit que anadio `POST /v1/payment-intents/&#123;id&#125;/reverse`
 * no toco el widget, y quien reabriera el enlace de un checkout cobrado DESPUES de que el comercio
 * lo anulara se encontraba el widget roto.
 */
export type IntentStatus =
  | 'CREATED'
  | 'REQUIRES_ACTION'
  | 'PROCESSING'
  | 'PENDING_AUTHORIZATION'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELED'
  | 'REVERSED';

const INTENT_STATUSES: readonly string[] = [
  'CREATED',
  'REQUIRES_ACTION',
  'PROCESSING',
  'PENDING_AUTHORIZATION',
  'SUCCEEDED',
  'FAILED',
  'EXPIRED',
  'CANCELED',
  'REVERSED',
];

/** Personalizacion visual del tenant, ya validada (ver theme/applyTenantTheme.ts). */
export interface TenantTheme {
  primary_color?: string | undefined;
  radius?: string | undefined;
  logo_url?: string | undefined;
}

/** Elemento de `checkout.payment_methods` del contrato OpenAPI. */
export interface CheckoutPaymentMethod {
  gateway_id: string;
  /** payment-api lo entrega dentro de `public_config.display_name`. */
  display_name?: string | undefined;
  /** Configuracion publica NO sensible de la pasarela (nunca credenciales). */
  public_config: Record<string, string>;
}

/** Resumen no sensible del medio de pago (`PaymentMethodSummary` del contrato). */
export interface PaymentMethodSummary {
  type?: string | undefined;
  brand?: string | undefined;
  last4?: string | undefined;
  exp_month?: number | undefined;
  exp_year?: number | undefined;
}

/** Bloque `checkout`, presente solo al autenticar con pk_ + X-Client-Secret. */
export interface PaymentIntentCheckout {
  /** Pasarela activa de la sesion; se deriva de `PaymentIntent.gateway_id`. */
  gateway_id: string;
  /** Origenes autorizados a embeber el widget; vacio = denegar (fail closed). */
  allowed_origins: string[];
  theme?: TenantTheme | undefined;
  /** URL de la pasarela cuando el intent esta en REQUIRES_ACTION. */
  redirect_url?: string | undefined;
  /** Clave publicable de la pasarela activa (`publishable_key` / `public_key`). */
  gateway_public_key?: string | undefined;
  /** Identificador de sesion de la pasarela, cuando la pasarela lo expone. */
  gateway_session_id?: string | undefined;
  tenant_name?: string | undefined;
  logo_url?: string | undefined;
  payment_methods?: CheckoutPaymentMethod[] | undefined;
}

/** Ultimo error conocido del intent, si la API lo expone. */
export interface PaymentIntentError {
  code: string;
  message: string;
}

/**
 * Una linea del desglose del cobro, tal como la emite el API (`LineItemView` del contrato).
 *
 * <p>`total_price_minor` viene YA CALCULADO por el servidor y es el que se pinta. El widget no
 * multiplica: si lo hiciera, dos capas podrian redondear distinto y el comprador veria un total de
 * linea que no coincide con el que el servidor sumo para validar el desglose.
 *
 * <p>Se llama `total_price_minor` y no `amount_minor` para que no se confunda con el del intent,
 * que es el total del COBRO. Los dos se pintan en la misma pantalla, uno por linea y otro abajo.
 */
export interface PaymentIntentLineItem {
  name: string;
  quantity: number;
  unit_price_minor: number;
  total_price_minor: number;
}

/** Payment intent tal como lo usa la UI del widget. */
export interface PaymentIntent {
  id: string;
  status: IntentStatus;
  amount_minor: number;
  currency: string;
  description?: string | undefined;
  /**
   * Desglose de lo que se paga. Ausente cuando el comercio no lo declaro; el API omite la clave en
   * vez de mandar un array vacio, y aqui se respeta esa distincion.
   */
  line_items?: PaymentIntentLineItem[] | undefined;
  /**
   * Instante de expiracion en ISO 8601. Puede faltar --un intent sin caducidad-- y entonces llega
   * como cadena vacia, asi que la UI comprueba `length > 0` antes de formatearlo.
   */
  expires_at: string;
  checkout: PaymentIntentCheckout;
  last_error?: PaymentIntentError | undefined;
  /** Vuelta a la tienda tras el desenlace; la declara el comercio al crear el intent. */
  merchant_return_url?: string | undefined;
  /** Campos del contrato OpenAPI que la UI puede mostrar tal cual. */
  gateway_id?: string | undefined;
  livemode?: boolean | undefined;
  created_at?: string | undefined;
  payment_method?: PaymentMethodSummary | undefined;
  metadata?: Record<string, string> | undefined;
}

/** Error RFC 9457 con el miembro de extension `code` estable de ApiPay. */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string | undefined;
  code: string;
}

/**
 * Como llevar al titular al formulario de la pasarela para inscribir una tarjeta.
 *
 * <p>Lleva `method` y `field_name` y no solo una URL porque **el formulario de inscripcion de
 * Transbank no se abre con un GET**: hay que hacer un POST con el token en un campo oculto cuyo
 * nombre lo decide la pasarela. Espeja `InscriptionResponse.RedirectResponse` de payment-api.
 */
export interface InscriptionRedirect {
  url: string;
  /** Verbo del formulario; en Oneclick es siempre POST. */
  method: string;
  /** Nombre del campo oculto que transporta el token (p. ej. `TBK_TOKEN`). */
  field_name: string;
  token: string;
}

/**
 * Inscripcion recien abierta sobre el intent.
 *
 * <p>PCI DSS SAQ A: aqui no hay ni puede haber dato de tarjeta. Lo unico que viaja es el
 * identificador `ins_...` y el token opaco con el que la pasarela reconoce SU formulario.
 */
export interface OpenedInscription {
  id: string;
  /**
   * Nulo cuando la respuesta no trae formulario. Ocurre si la API reproduce una inscripcion ya
   * abierta por idempotencia: devuelve la original, no un formulario nuevo.
   */
  redirect: InscriptionRedirect | null;
}

/**
 * Error que transporta un `ProblemDetails` hasta la estrategia de pasarela.
 *
 * <p>Existe porque hay dos formas legitimas de fallar y no son la misma: cuando se cae la CARGA de
 * la sesion no hay checkout que mostrar y el problema pertenece a la pantalla entera (por eso el
 * hook lo publica en `problem`); cuando falla una operacion que el comprador acaba de disparar, el
 * checkout sigue en pie y lo que toca es un aviso al lado del boton para que reintente. Estas
 * operaciones RECHAZAN con este error en vez de tumbar la sesion.
 *
 * <p>Expone `code` y `detail` como miembros propios para que `problemCodeOf` / `problemDetailOf`
 * lo lean igual que leen los errores de cualquier otra procedencia.
 */
export class ApiProblemError extends Error {
  readonly problem: ProblemDetails;
  readonly code: string;
  readonly detail: string;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiProblemError';
    this.problem = problem;
    this.code = problem.code;
    this.detail = problem.detail ?? problem.title;
  }
}

/** Claves de `public_config` donde cada pasarela publica su clave publicable. */
const PUBLIC_KEY_KEYS: readonly string[] = ['publishable_key', 'public_key', 'gateway_public_key'];

/** Claves de `public_config` que identifican la sesion creada en la pasarela. */
const SESSION_ID_KEYS: readonly string[] = [
  'gateway_session_id',
  'session_id',
  'preference_id',
  'checkout_session_id',
];

/** Clave donde payment-api empaqueta el nombre comercial de la pasarela. */
const DISPLAY_NAME_KEY = 'display_name';

/** Codigos de error del catalogo de CONVENCIONES 2 que el widget sabe traducir. */
const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  'validation_error',
  'invalid_api_key',
  'invalid_client_secret',
  'insufficient_permissions',
  'resource_not_found',
  'card_declined',
  'gateway_rejected',
  'invalid_state_transition',
  'currency_not_supported',
  'amount_below_minimum',
  'rate_limited',
  'gateway_unavailable',
  'internal_error',
  'network_error',
  'session_invalid',
  'payment_failed',
  'intent_expired',
  'embed_origin_denied',
]);

/** Codigo estable por defecto segun el status HTTP cuando el cuerpo no trae `code`. */
const STATUS_FALLBACK_CODE: Record<number, string> = {
  400: 'validation_error',
  401: 'invalid_client_secret',
  402: 'gateway_rejected',
  403: 'insufficient_permissions',
  404: 'resource_not_found',
  409: 'invalid_state_transition',
  422: 'validation_error',
  429: 'rate_limited',
  503: 'gateway_unavailable',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Normaliza el desglose descartando lo que no se puede pintar.
 *
 * <p>Una linea sin nombre, sin numeros o con numeros no finitos se DESCARTA en silencio, y el resto
 * se pinta: el desglose es informacion de apoyo, y romper la pantalla de pago por una linea mal
 * formada seria cambiar un problema cosmetico por un cobro perdido. Si no queda ninguna linea
 * utilizable se devuelve null, que es lo mismo que no traer desglose.
 */
function normalizeLineItems(value: unknown): PaymentIntentLineItem[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const items: PaymentIntentLineItem[] = [];
  for (const candidate of value) {
    if (typeof candidate !== 'object' || candidate === null) {
      continue;
    }
    const source = candidate as Record<string, unknown>;
    const name = asNonEmptyString(source['name']);
    const quantity = asFiniteNumber(source['quantity']);
    const unitPriceMinor = asFiniteNumber(source['unit_price_minor']);
    const totalPriceMinor = asFiniteNumber(source['total_price_minor']);
    if (name === null || quantity === null || unitPriceMinor === null || totalPriceMinor === null) {
      continue;
    }
    items.push({
      name,
      quantity,
      unit_price_minor: unitPriceMinor,
      total_price_minor: totalPriceMinor,
    });
  }
  return items.length > 0 ? items : null;
}

function asStringMap(value: unknown): Record<string, string> {
  const source = asRecord(value);
  if (source === null) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(source)) {
    if (typeof entry === 'string') {
      result[key] = entry;
    }
  }
  return result;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

function asIntentStatus(value: unknown): IntentStatus | null {
  if (typeof value !== 'string' || !INTENT_STATUSES.includes(value)) {
    return null;
  }
  return value as IntentStatus;
}

function firstConfigValue(
  config: Record<string, string>,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = config[key];
    if (value !== undefined && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

/** Clave publicable de la pasarela dentro de su `public_config`. */
export function publicKeyFromConfig(config: Record<string, string>): string | undefined {
  return firstConfigValue(config, PUBLIC_KEY_KEYS);
}

/** Identificador de sesion de la pasarela dentro de su `public_config`. */
export function sessionIdFromConfig(config: Record<string, string>): string | undefined {
  return firstConfigValue(config, SESSION_ID_KEYS);
}

function normalizePaymentMethods(value: unknown): CheckoutPaymentMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const methods: CheckoutPaymentMethod[] = [];
  for (const entry of value) {
    const source = asRecord(entry);
    if (source === null) {
      continue;
    }
    const gatewayId = asNonEmptyString(source['gateway_id']);
    if (gatewayId === null) {
      continue;
    }
    const publicConfig = asStringMap(source['public_config']);
    const displayName =
      asNonEmptyString(source['display_name']) ?? asNonEmptyString(publicConfig[DISPLAY_NAME_KEY]);
    const method: CheckoutPaymentMethod = { gateway_id: gatewayId, public_config: publicConfig };
    if (displayName !== null) {
      method.display_name = displayName;
    }
    methods.push(method);
  }
  return methods;
}

function normalizeCheckout(
  raw: unknown,
  topLevelGatewayId: string | null,
  topLevelRedirectUrl: string | null,
): PaymentIntentCheckout {
  const source = asRecord(raw) ?? {};
  const methods = normalizePaymentMethods(source['payment_methods']);
  const gatewayId = topLevelGatewayId ?? methods[0]?.gateway_id ?? '';
  const active = methods.find((method) => method.gateway_id === gatewayId) ?? methods[0];
  const config = active?.public_config ?? {};

  const checkout: PaymentIntentCheckout = {
    gateway_id: gatewayId,
    allowed_origins: asStringArray(source['allowed_origins']),
    payment_methods:
      methods.length > 0 || gatewayId.length === 0
        ? methods
        : // Sin bloque payment_methods la sesion sigue teniendo una pasarela: se
          // sintetiza para que el selector y las estrategias tengan de donde leer.
          [{ gateway_id: gatewayId, public_config: {} }],
  };

  const theme = sanitizeTenantTheme(source['theme']);
  if (theme !== undefined) {
    checkout.theme = theme;
  }
  if (topLevelRedirectUrl !== null) {
    checkout.redirect_url = topLevelRedirectUrl;
  }
  const gatewayPublicKey = publicKeyFromConfig(config);
  if (gatewayPublicKey !== undefined) {
    checkout.gateway_public_key = gatewayPublicKey;
  }
  const gatewaySessionId = sessionIdFromConfig(config);
  if (gatewaySessionId !== undefined) {
    checkout.gateway_session_id = gatewaySessionId;
  }
  const tenantName = asNonEmptyString(source['tenant_name']);
  if (tenantName !== null) {
    checkout.tenant_name = tenantName;
  }
  const logoUrl = sanitizeLogoUrl(source['logo_url']) ?? checkout.theme?.logo_url ?? null;
  if (logoUrl !== null) {
    checkout.logo_url = logoUrl;
  }

  return checkout;
}

function normalizeLastError(value: unknown): PaymentIntentError | undefined {
  const source = asRecord(value);
  if (source === null) {
    return undefined;
  }
  const code = asNonEmptyString(source['code']);
  const message = asNonEmptyString(source['message']);
  return code !== null && message !== null ? { code, message } : undefined;
}

/**
 * Proyecta la respuesta cruda de `GET|POST /v1/payment-intents/{id}` a la forma
 * interna del widget. Devuelve null si el cuerpo no es un payment intent utilizable.
 */
export function normalizePaymentIntent(raw: unknown): PaymentIntent | null {
  const source = asRecord(raw);
  if (source === null) {
    return null;
  }
  const id = asNonEmptyString(source['id']);
  const status = asIntentStatus(source['status']);
  const amountMinor = asFiniteNumber(source['amount_minor']);
  const currency = asNonEmptyString(source['currency']);
  if (id === null || status === null || amountMinor === null || currency === null) {
    return null;
  }

  const gatewayId = asNonEmptyString(source['gateway_id']);
  const redirectUrl = asNonEmptyString(source['redirect_url']);

  const intent: PaymentIntent = {
    id,
    status,
    amount_minor: amountMinor,
    currency: currency.toUpperCase(),
    expires_at: asNonEmptyString(source['expires_at']) ?? '',
    checkout: normalizeCheckout(source['checkout'], gatewayId, redirectUrl),
  };

  const description = asNonEmptyString(source['description']);
  if (description !== null) {
    intent.description = description;
  }
  const lineItems = normalizeLineItems(source['line_items']);
  if (lineItems !== null) {
    intent.line_items = lineItems;
  }
  const lastError = normalizeLastError(source['last_error']);
  if (lastError !== undefined) {
    intent.last_error = lastError;
  }
  if (gatewayId !== null) {
    intent.gateway_id = gatewayId;
  }
  const merchantReturnUrl = asNonEmptyString(source['merchant_return_url']);
  if (merchantReturnUrl !== null) {
    intent.merchant_return_url = merchantReturnUrl;
  }
  if (typeof source['livemode'] === 'boolean') {
    intent.livemode = source['livemode'];
  }
  const createdAt = asNonEmptyString(source['created_at']);
  if (createdAt !== null) {
    intent.created_at = createdAt;
  }
  const paymentMethod = asRecord(source['payment_method']);
  if (paymentMethod !== null) {
    intent.payment_method = normalizePaymentMethodSummary(paymentMethod);
  }
  const metadata = asStringMap(source['metadata']);
  if (Object.keys(metadata).length > 0) {
    intent.metadata = metadata;
  }

  return intent;
}

/**
 * Instrucciones de redireccion de una inscripcion. Sin las tres piezas obligatorias —URL, nombre
 * del campo y token— no hay formulario que emitir, asi que se devuelve null en vez de un objeto a
 * medias con el que el navegador haria un POST incompleto.
 */
function normalizeInscriptionRedirect(raw: unknown): InscriptionRedirect | null {
  const source = asRecord(raw);
  if (source === null) {
    return null;
  }
  const url = asNonEmptyString(source['url']);
  const fieldName = asNonEmptyString(source['field_name']);
  const token = asNonEmptyString(source['token']);
  if (url === null || fieldName === null || token === null) {
    return null;
  }
  return {
    url,
    // El verbo lo dicta la pasarela, pero el unico que abre el formulario de Transbank es POST:
    // ante una respuesta sin `method` se asume ese y no un GET, que devolveria 405.
    method: asNonEmptyString(source['method']) ?? 'POST',
    field_name: fieldName,
    token,
  };
}

/**
 * Proyecta la respuesta de `POST /v1/payment-intents/{id}/inscriptions` a la forma interna.
 *
 * <p>El contrato es el bloque anidado `redirect` de `InscriptionResponse`. Se acepta ademas la forma
 * PLANA con los cuatro campos en la raiz, y no por indecision: una build vieja cacheada en el
 * navegador puede encontrarse con una API mas nueva, y el precio de no encajar lo paga el comprador,
 * que se queda sin poder inscribir su tarjeta pese a que el servidor hizo su trabajo.
 */
export function normalizeOpenedInscription(raw: unknown): OpenedInscription | null {
  const source = asRecord(raw);
  if (source === null) {
    return null;
  }
  const id = asNonEmptyString(source['id']);
  if (id === null) {
    return null;
  }
  const redirect =
    normalizeInscriptionRedirect(source['redirect']) ?? normalizeInscriptionRedirect(source);
  return { id, redirect };
}

function normalizePaymentMethodSummary(source: Record<string, unknown>): PaymentMethodSummary {
  const summary: PaymentMethodSummary = {};
  const type = asNonEmptyString(source['type']);
  if (type !== null) {
    summary.type = type;
  }
  const brand = asNonEmptyString(source['brand']);
  if (brand !== null) {
    summary.brand = brand;
  }
  const last4 = asNonEmptyString(source['last4']);
  if (last4 !== null) {
    summary.last4 = last4;
  }
  const expMonth = asFiniteNumber(source['exp_month']);
  if (expMonth !== null) {
    summary.exp_month = expMonth;
  }
  const expYear = asFiniteNumber(source['exp_year']);
  if (expYear !== null) {
    summary.exp_year = expYear;
  }
  return summary;
}

/**
 * Construye el ProblemDetails a partir del cuerpo `application/problem+json`.
 * El `code` es el miembro de extension estable: la UI decide por el, jamas por
 * el texto de `detail`, que puede cambiar sin aviso.
 */
export function problemFromPayload(payload: unknown, httpStatus: number): ProblemDetails {
  const source = asRecord(payload);
  const code =
    asNonEmptyString(source?.['code']) ?? STATUS_FALLBACK_CODE[httpStatus] ?? 'internal_error';
  const problem: ProblemDetails = {
    type: asNonEmptyString(source?.['type']) ?? 'about:blank',
    title: asNonEmptyString(source?.['title']) ?? 'Error',
    status: asFiniteNumber(source?.['status']) ?? httpStatus,
    code,
  };
  const detail = asNonEmptyString(source?.['detail']);
  if (detail !== null) {
    problem.detail = detail;
  }
  return problem;
}

/** Problema sintetico cuando el fetch ni siquiera llega a la API. */
export function networkProblem(): ProblemDetails {
  return {
    type: 'about:blank',
    title: 'Network error',
    status: 0,
    code: 'network_error',
  };
}

/** Problema sintetico cuando la API responde 200 con un cuerpo inutilizable. */
export function invalidResponseProblem(): ProblemDetails {
  return {
    type: 'about:blank',
    title: 'Invalid response',
    status: 502,
    code: 'internal_error',
  };
}

/** Problema sintetico cuando el client_secret de la sesion no es utilizable. */
export function invalidClientSecretProblem(): ProblemDetails {
  return {
    type: 'about:blank',
    title: 'Invalid client secret',
    status: 401,
    code: 'invalid_client_secret',
  };
}

/** Clave i18n del mensaje asociado a un `code`; `error.unknown` si no se conoce. */
export function problemMessageKey(code: string): string {
  return KNOWN_ERROR_CODES.has(code) ? `error.${code}` : 'error.unknown';
}
